import { useEffect, useState } from 'react'

const SCRIPT_SRC = 'https://js.chargebee.com/v2/chargebee.js'
const SCRIPT_ID = 'chargebee-js-sdk'
const LOCAL_MOCK_SITE = 'mock'
const LOCAL_MOCK_HOSTED_PAGE_ID = 'hosted_local_mock'
const LOCAL_MOCK_HOSTNAMES = new Set([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '::1',
  '[::1]',
  'host.docker.internal',
])

type CbCheckoutCallbacks = {
  loaded?: () => void
  success?: (data: { id: string }) => void
  close?: () => void
  step?: (step: string) => void
  error?: (err: unknown) => void
  resize?: (size: { height: number; width: number } | number) => void
}

export type CbCheckout = {
  mount: (containerSelector: string | HTMLElement) => void
}

export type CbInstance = {
  createCheckout: (opts: {
    url?: string
    callbacks?: CbCheckoutCallbacks
  }) => Promise<CbCheckout>
}

type CbGlobal = {
  init: (opts: { site: string }) => CbInstance
}

declare global {
  interface Window {
    Chargebee?: CbGlobal
  }
}

export type ChargebeeSdkStatus = 'loading' | 'ready' | 'error'

type State = {
  status: ChargebeeSdkStatus
  cbInstance?: CbInstance
}

let modulePromise: Promise<void> | null = null
let moduleInstance: CbInstance | undefined
let moduleSite: string | undefined
let subscribers: Array<(s: State) => void> = []
let currentState: State = { status: 'loading' }

function setState(next: State): void {
  if (
    next.status === currentState.status &&
    next.cbInstance === currentState.cbInstance
  ) {
    return
  }
  currentState = next
  subscribers.forEach((fn) => fn(next))
}

function loadScript(): Promise<void> {
  if (modulePromise) return modulePromise
  modulePromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      if (window.Chargebee) {
        resolve()
      } else {
        existing.addEventListener('load', () => resolve())
        existing.addEventListener('error', () => reject(new Error('load')))
      }
      return
    }
    const tag = document.createElement('script')
    tag.id = SCRIPT_ID
    tag.src = SCRIPT_SRC
    tag.async = true
    tag.onload = (): void => resolve()
    tag.onerror = (): void => reject(new Error('load'))
    document.head.appendChild(tag)
  })
  return modulePromise
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return Boolean(v) && typeof v === 'object'
}

function getCheckoutOrigin(url?: string): string | null {
  if (!url) return null
  try {
    return new URL(url, window.location.href).origin
  } catch {
    return null
  }
}

export function isLocalMockHostname(hostname: string): boolean {
  return LOCAL_MOCK_HOSTNAMES.has(hostname) || hostname.endsWith('.nip.io')
}

function isAllowedLocalMockMessageOrigin(
  eventOrigin: string,
  checkoutOrigin: string | null,
): boolean {
  if (!checkoutOrigin) return false
  if (eventOrigin === checkoutOrigin) return true

  try {
    const eventUrl = new URL(eventOrigin)
    const checkoutUrl = new URL(checkoutOrigin)

    return (
      eventUrl.protocol === checkoutUrl.protocol &&
      eventUrl.port === checkoutUrl.port &&
      isLocalMockHostname(eventUrl.hostname) &&
      isLocalMockHostname(checkoutUrl.hostname)
    )
  } catch {
    return false
  }
}

function getSuccessData(data: unknown): { id: string } {
  if (isRecord(data)) {
    const value = data.value
    if (isRecord(value) && typeof value.id === 'string') {
      return { id: value.id }
    }

    const nested = data.data
    if (isRecord(nested) && typeof nested.id === 'string') {
      return { id: nested.id }
    }
  }

  return { id: LOCAL_MOCK_HOSTED_PAGE_ID }
}

function getErrorMessage(data: unknown): string {
  if (isRecord(data)) {
    const value = data.value
    if (isRecord(value) && typeof value.message === 'string') {
      return value.message
    }

    const nested = data.data
    if (isRecord(nested) && typeof nested.message === 'string') {
      return nested.message
    }

    if (typeof data.message === 'string') {
      return data.message
    }
  }

  return 'Local Chargebee checkout failed'
}

function createLocalMockInstance(): CbInstance {
  return {
    createCheckout: (opts) =>
      Promise.resolve({
        mount: (containerSelector): void => {
          const container =
            typeof containerSelector === 'string'
              ? document.querySelector<HTMLElement>(containerSelector)
              : containerSelector

          if (!container) {
            opts.callbacks?.error?.(
              new Error(`Chargebee mock container not found: ${containerSelector}`),
            )
            return
          }

          const iframe = document.createElement('iframe')
          iframe.name = 'cb-checkout-frame'
          iframe.title = 'Chargebee checkout'
          iframe.src = opts.url ?? 'about:blank'
          iframe.style.border = '0'
          iframe.style.height = '100%'
          iframe.style.width = '100%'

          const checkoutOrigin = getCheckoutOrigin(opts.url)
          if (!checkoutOrigin) {
            opts.callbacks?.error?.(
              new Error('Local Chargebee checkout requires a valid hosted page URL'),
            )
            return
          }

          function teardown(): void {
            window.removeEventListener('message', onMessage)
            iframe.remove()
          }

          function onMessage(event: MessageEvent): void {
            if (
              !isAllowedLocalMockMessageOrigin(event.origin, checkoutOrigin)
            ) {
              return
            }

            const { data } = event
            if (data === 'loaded') {
              opts.callbacks?.loaded?.()
              return
            }

            if (data === 'close' || data === 'frame_unloaded') {
              opts.callbacks?.close?.()
              teardown()
              return
            }

            if (data === 'error' || (isRecord(data) && data.key === 'error')) {
              opts.callbacks?.error?.(new Error(getErrorMessage(data)))
              teardown()
              return
            }

            if (data === 'success' || (isRecord(data) && data.key === 'success')) {
              opts.callbacks?.success?.(getSuccessData(data))
              teardown()
            }
          }

          window.addEventListener('message', onMessage)
          container.replaceChildren(iframe)
        },
      }),
  }
}

export function __resetForTests(): void {
  modulePromise = null
  moduleInstance = undefined
  moduleSite = undefined
  subscribers = []
  currentState = { status: 'loading' }
}

export default function useChargebeeSdk(cfg: { site: string }): State {
  const [state, setLocal] = useState<State>(currentState)

  useEffect(() => {
    subscribers.push(setLocal)
    setLocal(currentState)

    const unsubscribe = (): void => {
      subscribers = subscribers.filter((fn) => fn !== setLocal)
    }

    if (cfg.site === LOCAL_MOCK_SITE) {
      if (!isLocalMockHostname(window.location.hostname)) {
        setState({ status: 'error' })
        return unsubscribe
      }

      modulePromise = null
      moduleInstance = createLocalMockInstance()
      moduleSite = cfg.site
      setState({ status: 'ready', cbInstance: moduleInstance })
      return unsubscribe
    }

    loadScript()
      .then(() => {
        if (!window.Chargebee) {
          setState({ status: 'error' })
          return
        }
        if (!moduleInstance || moduleSite === LOCAL_MOCK_SITE) {
          // TODO(backend): Chargebee's SDK redirects to the plan's
          // redirect_url after checkout success, even with
          // enableTopLevelRedirectOnSuccess:false. The redirect is
          // performed by chargebee.js in our window context, so we
          // can't block it from the frontend. The backend needs to
          // either omit redirect_url when creating hosted pages for
          // embedded checkout, or the redirect URL should be removed
          // from the plan configuration in the Chargebee dashboard.
          moduleInstance = window.Chargebee.init({
            site: cfg.site,
          })
          moduleSite = cfg.site
        }
        setState({ status: 'ready', cbInstance: moduleInstance })
      })
      .catch(() => setState({ status: 'error' }))

    return unsubscribe
  }, [cfg.site])

  return state
}
